import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, X, Check, CheckCheck } from "lucide-react";
import { over } from 'stompjs';
import SockJS from 'sockjs-client';
import './chatRoom.css';
import axiosInstance from '../../API/axiosInstance';

var stompClient = null;

const predefinedMessages = {
    "requirements": [
        "Can you share your project requirements in detail?",
        "What are the main features you’re looking for?",
        "Do you have any reference projects in mind?",
        "Would you like to provide a project document?"
    ],
    "budget": [
        "What is your estimated budget for this project?",
        "Are you open to a flexible budget based on features?",
        "Would you like a cost breakdown for different options?",
        "Are there any cost constraints we should consider?"
    ],
    "technology": [
        "Do you have any preferred technology stack?",
        "Are you looking for a mobile or web-based solution?",
        "Would you like recommendations on the best technology?",
        "Should we consider AI, ML, or automation for efficiency?"
    ],
    "timeline": [
        "When do you need this project completed?",
        "Is there a strict deadline we should meet?",
        "Would you like to discuss a phased delivery plan?",
        "Do you need an MVP first before full development?"
    ],
    "communication": [
        "Would you like to schedule a call to discuss details?",
        "Do you prefer email, WhatsApp, or direct calls for updates?",
        "Shall we set up a recurring weekly progress meeting?",
        "What time zone are you in for smooth collaboration?"
    ],
    "payment": [
        "What is your preferred payment mode?",
        "Shall we sign an NDA before discussing details?",
        "Would you like milestone-based payments?",
        "Do you have specific contract terms we should review?"
    ]
};

export default function ChatRoom({ vendor, user }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [token, setToken] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    const [suggestedMessages, setSuggestedMessages] = useState([]);
    const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
    const [isToolTip, setIsToolTip] = useState(true);

    const chatSectionRef = useRef(null);

    const [userData, setUserData] = useState({
        senderName: user?.id || "",
        receivername: vendor?.id || "",
        connected: false,
        date: null,
        message: '',
        sName: user?.username || "",
        rName: vendor?.business_name || ""
    });

    const toggleChat = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        if (newIsOpen && unreadCount > 0) {
            markMessagesAsRead();
        }
    };
    
    const toggleSuggestion = () => setIsSuggestionOpen(!isSuggestionOpen);
    const toggleTooltip = () => setIsToolTip(false);

    const fetchMessages = async (senderName, receiverName) => {
        try {
            if (!token) return;
            const response1 = await axiosInstance.get(`/api/messages?senderName=${senderName}&receiverName=${receiverName}`, { headers: { "Authorization": `Bearer ${token}` } });
            const response2 = await axiosInstance.get(`/api/messages?senderName=${receiverName}&receiverName=${senderName}`, { headers: { "Authorization": `Bearer ${token}` } });
            const messages1 = Array.isArray(response1.data) ? response1.data : [];
            const messages2 = Array.isArray(response2.data) ? response2.data : [];
            const allMessages = [...messages1, ...messages2].sort((a, b) => new Date(a.date) - new Date(b.date));
            setMessages(allMessages);
            const initialUnreadCount = allMessages.filter(msg => msg.receiverName === userData.senderName && !msg.isRead).length;
            setUnreadCount(initialUnreadCount);
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };
    
    const markMessagesAsRead = () => {
        const unreadMessageIds = messages.filter(msg => msg.receiverName === userData.senderName && !msg.isRead).map(msg => msg.id);
        if (unreadMessageIds.length > 0 && stompClient?.connected) {
            stompClient.send("/app/mark-as-read", {}, JSON.stringify({ messageIds: unreadMessageIds }));
            setMessages(prev => prev.map(msg => unreadMessageIds.includes(msg.id) ? { ...msg, isRead: true } : msg));
            setUnreadCount(0);
        }
    };

    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        if (storedToken) setToken(storedToken);
    }, []);

    useEffect(() => {
        if (token && userData.senderName && userData.receivername) {
            fetchMessages(userData.senderName, userData.receivername);
            connect();
        }
        return () => {
            if (stompClient?.connected) {
                stompClient.disconnect(() => console.log("WebSocket disconnected!"));
            }
        }
    }, [token, userData.senderName, userData.receivername]);

    const connect = () => {
        let Sock = new SockJS(`https://wedstra-backend-9886.onrender.com/ws`);
        // let Sock = new SockJS(`http://localhost:8443/ws`);
        stompClient = over(Sock);
        stompClient.connect({}, onConnected, onError);
    };

    const onConnected = () => {
        setUserData(prev => ({ ...prev, connected: true }));
        stompClient.subscribe('/user/' + userData.senderName + '/private', onPrivateMessage);
        stompClient.subscribe('/user/' + userData.senderName + '/messages-read', onMessagesRead);
        userJoin();
    };

    const onMessagesRead = (payload) => {
        const { messageIds } = JSON.parse(payload.body);
        setMessages(prev => prev.map(msg => messageIds.includes(msg.id) ? { ...msg, isRead: true } : msg));
    };

    const userJoin = () => {
        if (stompClient?.connected) {
            stompClient.send("/app/message", {}, JSON.stringify({ senderName: userData.senderName, status: "JOIN" }));
        }
    };

    const onPrivateMessage = (payload) => {
        const newMessage = JSON.parse(payload.body);
        if (newMessage.senderName === userData.senderName) {
            return;
        }

        setMessages(prev => [...prev, newMessage]);
        if (isOpen) {
             if (stompClient?.connected) {
                stompClient.send("/app/mark-as-read", {}, JSON.stringify({ messageIds: [newMessage.id] }));
            }
        } else {
            setUnreadCount(prev => prev + 1);
        }
    };

    const onError = (err) => console.log("WebSocket Error:", err);

    const handleMessage = (event) => {
        const value = event.target.value;
        setMessage(value);
        setUserData(prev => ({ ...prev, message: value }));
    };

    const sendPrivateValue = () => {
        if (stompClient?.connected && userData.message.trim()) {
            const chatMessage = {
                senderName: userData.senderName,
                receiverName: userData.receivername,
                message: userData.message,
                sName: userData.sName,
                rName: userData.rName,
                date: new Date().toISOString(),
                status: "MESSAGE",
                isRead: false
            };
            
            setMessages(prev => [...prev, chatMessage]);
            stompClient.send("/app/private-message", {}, JSON.stringify(chatMessage));
            setUserData(prev => ({ ...prev, message: "" }));
            setMessage("");
            setIsSuggestionOpen(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            sendPrivateValue();
        }
    };

    useEffect(() => {
        const words = userData.message.toLowerCase().split(" ");
        const lastWord = words[words.length - 1];

        if (predefinedMessages[lastWord]) {
            setSuggestedMessages(predefinedMessages[lastWord]);
            setIsSuggestionOpen(true);
        }
    }, [message]);

    const handleSuggestionClick = (msg) => {
        setUserData(prev => ({ ...prev, message: msg }));
        setMessage(msg);
        setIsSuggestionOpen(false);
    };

    useEffect(() => {
        if (chatSectionRef.current) {
            chatSectionRef.current.scrollTop = chatSectionRef.current.scrollHeight;
        }
    }, [messages]);

    return (
        <div>
            {/* Floating Chat Button */}
            {!isOpen && (
                <div className="position-fixed bottom-3 start-3 d-flex align-items-center gap-2 p-2 bg-white shadow-lg rounded-pill border" id="chat-btn" onClick={toggleChat}>
                    {unreadCount > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                            {unreadCount}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                    <img src="https://cdn-icons-png.flaticon.com/512/6596/6596121.png" alt="User" className="rounded-circle" width="40" height="40" />
                    <div className="d-flex flex-column">
                        <strong>Message <br />{vendor.business_name}</strong>
                    </div>
                    <button className="btn btn-light rounded-circle shadow-sm p-2">
                        <MessageSquare size={18} />
                    </button>
                </div>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className="card shadow-lg border rounded-4 d-flex flex-column" id="chat-window" style={{ width: "400px", height: "500px" }}>
                    {/* Card Header */}
                    <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom w-100">
                        <div className="d-flex align-items-center">
                            <img src="https://cdn-icons-png.flaticon.com/512/6596/6596121.png" alt="User" className="rounded-circle me-2" width="40" height="40" />
                            <div>
                                <strong>Message {vendor.business_name}</strong>
                            </div>
                        </div>
                        <X size={20} className="cursor-pointer" id="close-btn" onClick={toggleChat} />
                    </div>
                    
                    {isToolTip && (
                        <div id="suggestion-tooltip" className="tooltip-box shadow-sm p-3 rounded">
                            <span>
                                Need help? Try typing keywords like <strong>"requirements"</strong>, <strong>"budget"</strong>, or <strong>"timeline"</strong> to get suggested messages.
                            </span>
                            <X size={20} className="cursor-pointer ms-2 text-danger close-btn" onClick={toggleTooltip} />
                        </div>
                    )}

                    {/* Chat Content */}
                    <div ref={chatSectionRef} className="flex-grow-1 overflow-auto p-3 w-100" id="chat-section">
                        {messages.map((chat, index) => (
                            <div key={chat.id || index} className={`d-flex flex-column mb-2 ${chat.senderName === userData.senderName ? "align-items-end" : "align-items-start"}`}>
                                <div className={`d-flex align-items-end ${chat.senderName === userData.senderName ? "flex-row-reverse" : ""}`}>
                                    {chat.senderName === userData.receivername && (
                                        <img src="https://cdn-icons-png.flaticon.com/512/4140/4140048.png" alt="Vendor" className="rounded-circle me-2" width="35" height="35" />
                                    )}
                                    <div className={`p-2 shadow-sm ${chat.senderName === userData.senderName ? "bg-primary text-white" : "bg-light text-dark"}`} style={{ maxWidth: "250px", padding: "8px 15px", borderRadius: chat.senderName === userData.senderName ? "18px 18px 2px 18px" : "18px 18px 18px 2px" }}>
                                        {chat.message}
                                    </div>
                                </div>
                                {chat.senderName === userData.senderName && (
                                    <div className="d-flex align-items-center text-muted small mt-1 me-2">
                                        {chat.isRead ? <CheckCheck size={16} className="text-info" /> : <Check size={16} />}
                                        <span className="ms-1">{new Date(chat.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {isSuggestionOpen && suggestedMessages.length > 0 && (
                        <div className="p-2 border-top bg-light position-relative">
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                <strong>Suggested Messages:</strong>
                                <X size={20} className="cursor-pointer" onClick={() => setIsSuggestionOpen(false)} />
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {suggestedMessages.map((msg, index) => (
                                    <button key={index} className="btn btn-outline-primary btn-sm" onClick={() => handleSuggestionClick(msg)}>
                                        {msg}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input Field */}
                     <div className="card-footer d-flex align-items-center p-2 bg-white border-top w-100">
                        <input type="text" className="form-control border-0" value={userData.message} onChange={handleMessage} placeholder="Type a message..." maxLength="2500" onKeyDown={handleKeyPress} />
                        <button className="btn btn-success ms-2" onClick={sendPrivateValue}>Send</button>
                    </div>
                </div>
            )}
        </div>
    );
}